import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTracker } from '../../contexts/TrackerContext';
import './Instructions.css';

function DeviceTaskIntro() {
  const navigate = useNavigate();
  const { tracker } = useTracker();

  const handleContinue = () => {
    tracker.track('deviceTaskIntroComplete', {
      timestamp: Date.now()
    });
    navigate('/device-task');
  };

  return (
    <div className="instructions-phase">
      <div className="instructions-content">
        <h1>💻 Device Security Task - Detailed Instructions</h1>

        <div className="instruction-section" style={{ backgroundColor: '#e3f2fd', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
          <h2>🎯 THE SCENARIO</h2>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Imagine you are at work, sitting at your workstation.
          </p>
          <p style={{ fontSize: '15px' }}>
            You will face <strong>4 simulated workplace technology situations</strong>. Imagine
            that each one is happening at your actual workplace.
          </p>
          <p style={{ fontSize: '15px', marginTop: '0.5rem' }}>
            Your task is to decide how you would handle each situation, <strong>exactly as you would in your real job</strong>.
          </p>
          <p style={{ fontSize: '15px', marginTop: '0.75rem' }}>
            <strong>Simulation note:</strong> Treat the dates and deadlines shown in each scenario
            as current within that simulated workday. Do not compare them with today&apos;s calendar date.
          </p>
        </div>

        <div className="instruction-section">
          <h2>📋 STEP-BY-STEP: What You Need To Do</h2>

          <div style={{ marginLeft: '1rem', marginTop: '1rem' }}>
            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 1:</strong> You will see a workplace technology scenario appear on your screen.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 2:</strong> <span style={{ backgroundColor: '#fff3cd', padding: '2px 6px' }}>Read the scenario carefully. Imagine this is happening to you right now at your workplace.</span>
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 3:</strong> Think about what you would do in this situation at your real job.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 4:</strong> You will see <strong>two action choices</strong>. The first carries out the action shown in the scenario; the second declines it. Click ONE to make your decision.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 5:</strong> After clicking a button, you'll be asked a quick question about WHY you made that choice. Answer it briefly.
            </p>

            <p style={{ fontSize: '15px', marginBottom: '1rem' }}>
              <strong>STEP 6:</strong> The next scenario will appear. Repeat steps 1-5 for all 4 scenarios.
            </p>
          </div>
        </div>

        <div className="instruction-section" style={{ backgroundColor: '#fff9e6', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem' }}>
          <h2>🔘 THE TWO CHOICES - What Each One Means</h2>
          <p style={{ marginBottom: '1rem' }}>The exact button labels will match each scenario, but they always represent these two choices:</p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', maxWidth: '500px', margin: '1.5rem auto' }}>
            {/* Proceed Button - Exact replica */}
            <button style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              border: 'none',
              borderRadius: '4px',
              cursor: 'default',
              minWidth: '200px',
              backgroundColor: '#3498db',
              color: 'white'
            }}>
              Proceed
            </button>

            {/* Decline Button - Exact replica */}
            <button style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              border: 'none',
              borderRadius: '4px',
              cursor: 'default',
              minWidth: '200px',
              backgroundColor: '#e74c3c',
              color: 'white'
            }}>
              Decline
            </button>
          </div>

          <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>"Proceed"</strong> (Blue button) - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>You would allow this to continue</li>
              <li>You would install the software or grant the access</li>
              <li>You would connect to this network or use this device</li>
              <li>You would follow through with the action</li>
            </ul>
          </div>

          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '6px' }}>
            <p style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <strong>"Decline"</strong> (Red button) - Click this if:
            </p>
            <ul style={{ marginLeft: '1.5rem', fontSize: '14px' }}>
              <li>Something about this situation doesn't feel right</li>
              <li>You would refuse this action because it seems unsafe or unverified</li>
              <li>You would want to verify before proceeding</li>
              <li>You're unsure and would rather not take the risk</li>
            </ul>
          </div>

          <p style={{ fontSize: '13px', color: '#605e5c', textAlign: 'center', fontStyle: 'italic', marginTop: '1.5rem', fontWeight: 'bold' }}>
            ⚠️ IMPORTANT: You MUST click one of these two buttons for every scenario to proceed to the next one.
          </p>
        </div>

        <div className="instruction-section" style={{ backgroundColor: '#f0f4c3', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem' }}>
          <h2>💡 REMEMBER</h2>
          <ul style={{ fontSize: '15px', lineHeight: '1.8' }}>
            <li><strong>This is YOUR workplace</strong> - Act like you normally would at your real job</li>
            <li><strong>Read each scenario carefully</strong> - Imagine it's really happening to you</li>
            <li><strong>Click ONE of the two action choices</strong> for every scenario</li>
            <li><strong>Answer the follow-up question</strong> after each decision</li>
            <li><strong>Complete all 4 scenarios</strong> to finish this task</li>
            <li><strong>No score will be shown during the task</strong> - choose what you would really do</li>
            <li><strong>Take your time</strong> - this is not a speed test</li>
          </ul>
        </div>

        <div className="instruction-section" style={{ marginTop: '1.5rem', textAlign: 'center', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            ✅ Ready to start? Click the button below when you're ready to see the first scenario.
          </p>
          <p style={{ fontSize: '14px', color: '#555' }}>
            Time estimate: 10-15 minutes
          </p>
        </div>

        <div className="button-container">
          <button onClick={handleContinue} className="btn btn-primary">
            Begin Device Security Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeviceTaskIntro;
